import { UseFormReturnType } from "@mantine/form";

import { getSignAndLockValidationMessage } from "@/encounters/utils/form-error-messages";
import { toast } from "@/utils/notifications";
import { captureExceptionWithoutPhi } from "@/utils/sentry";

/**
 * Runs an amendment's Sign & Lock: validates the form, signs it, and resets the form only once
 * the amendment is signed. A blocked or failed attempt keeps the provider's edits.
 *
 * @param form The amendment form.
 * @param signAmendment Signs the amendment. Resolves `true` only when the amendment was signed.
 */
export function signAndLockAmendment<Values>(
  form: UseFormReturnType<Values>,
  signAmendment: () => Promise<boolean>,
): void {
  const validation = form.validate();
  if (validation.hasErrors) {
    toast({
      title: "Validation Error",
      message: getSignAndLockValidationMessage(validation.errors),
      type: "error",
    });
    return;
  }
  form.onSubmit(async () => {
    const isSigned = await signAmendmentReportingFailure(signAmendment);
    if (isSigned) form.reset();
  })();
}

/**
 * Signs the amendment, turning a thrown failure into an error toast and a PHI-free report.
 *
 * @param signAmendment Signs the amendment.
 * @returns Whether the amendment was signed. A thrown failure counts as not signed.
 */
async function signAmendmentReportingFailure(
  signAmendment: () => Promise<boolean>,
): Promise<boolean> {
  try {
    return await signAmendment();
  } catch (error) {
    captureExceptionWithoutPhi("Failed to sign and lock an encounter amendment", error);
    toast({
      title: "Failed to amend encounter",
      message:
        "The amendment could not be saved. Your changes are still on the page; try Sign & Lock again.",
      type: "error",
      autoClose: false,
      id: "amend-encounter-failed",
    });
    return false;
  }
}

/**
 * Fails an amendment whose amend service saved nothing. The amend services return no response
 * only when they stopped before writing, so this is a failed save, not a blocked one.
 *
 * @param savedAmendment The amend service's `[questionnaireResponse, encounter]` result.
 * @returns The same pair, both present.
 */
export function requireSavedAmendment<Response, SavedEncounter>(
  savedAmendment: [Response | undefined, SavedEncounter | undefined] | [],
): [Response, SavedEncounter] {
  const [questionnaireResponse, encounter] = savedAmendment;
  if (!questionnaireResponse || !encounter) {
    throw new Error("The encounter amendment was not saved");
  }
  return [questionnaireResponse, encounter];
}
